// useSignUpDetailsForm.ts
import React from 'react'
import { useTranslation } from 'next-i18next'
import { useDeviceDetector, useFormValues, useIsResponsive } from '@/hooks'
import { useEMTDispatch } from '@/store'
import useConnectionRoutes from '@/hooks/useConnectionRoutes'
import { Routes } from '@/assets/configs'
import usePetSitterConnectionRoutes from '@/hooks/usePetSitterConnectionRoutes'
import useShadowOptins from '@/hooks/useShadowOptins'
import { AuthServiceHelper } from '@/modules/Auth'
import { UrlParamType } from '@/modules/utils'
import { Validator } from '@/utils'
import { checkSignUpStringFormat, isUserOnMobile } from 'emt-library'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { Address } from '@/dataV3/Common/Address'
import { Register } from '@/modules/Auth/apiTypes'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import { Constants } from '@/assets'
import { fbEvent } from '@rivercode/facebook-conversion-api-nextjs'
import { useRouter } from 'next/router'

import { UserServiceHelper } from '@/modules/User'
import { buildFakeAnimalBody } from '@/helpers/animal/buildFakeAnimalBody'
import { MessageEventData_Login, MessageEventData_TrackEvent, SODesktopScreenSlugs } from '@/data'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { containsPhoneNumber } from '@/utils/validationUtils/containsPhoneNumber'
import { PartnerOptins } from '@/modules/User/apiTypes'

const stubs = {
  firstName: process.env.NEXT_PUBLIC_STUBS_FIRSTNAME || '',
  lastName: process.env.NEXT_PUBLIC_STUBS_LASTNAME || '',
  emailOwner: process.env.NEXT_PUBLIC_STUBS_EMAIL_OWNER || '',
  emailBorrower: process.env.NEXT_PUBLIC_STUBS_EMAIL_BORROWER || '',
  password: process.env.NEXT_PUBLIC_STUBS_PASSWORD || '',
  plainPassword: process.env.NEXT_PUBLIC_STUBS_PLAINPASSWORD || '',
  phone: process.env.NEXT_PUBLIC_STUBS_PHONE || '',
}

export type FormValues = {
  firstName: string
  lastName: string
  email: string
  password: string
  plainPassword: string
  address: Address | null
  phone: string
  isOfAge: boolean
  answerRequests?: boolean
  purinaBeSentence?: string
  purinaBeOptin?: boolean
  purinaFrOptin?: boolean
  ultraPremiumOptin?: boolean
}

export type SignUpDetailsFormReturn = ReturnType<typeof useSignUpDetailsForm>

export function useSignUpDetailsForm(type: string) {
  const { t } = useTranslation('connection')
  const { appType } = useDeviceDetector()
  const dispatch = useEMTDispatch()

  const { isPetsittingMode } = usePetsittingMode()
  const [isMobile] = useIsResponsive()
  const rowGap = isMobile ? '10px' : '20px'

  const [formIsSend, setFormIsSend] = React.useState(false)
  const [isFocus, setIsFocus] = React.useState(false)
  const [isLoading, setLoading] = React.useState(false)

  const setAddress = useNewRequestCartStore((s) => s.setAddress)

  const { navigateToNextRoute, finalizeInscription } = useConnectionRoutes()
  const { goNextStep } = usePetSitterConnectionRoutes({ shouldInitialize: false })
  const router = useRouter()

  const { renderOptins, dispatchOptin } = useShadowOptins(
    SODesktopScreenSlugs.Register_About,
    {},
    { manualUpdate: true },
  )

  const postPartnerOptins = async (optinsToSave: PartnerOptins.UserPartnerOptinInput[] = []) => {
    console.log('Posting partner optins', optinsToSave)
    const results = await Promise.allSettled(
      optinsToSave.map((optin) => UserServiceHelper.createPartnerOptinAsync(optin)),
    )

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.warn('Partner optin post failed', {
          optinSlug: optinsToSave[index]?.optinSlug,
          error: (result.reason as any)?.message,
        })
      }
    })
  }

  // Flags derived from type
  const isQueryOwner = type === 'dogowner'
  const isQueryBorrower = type === 'borrower'
  const isQueryPetSitter = type === 'pro'

  const { values, errors, setValue, setError, manageNotificationError, isValid } = useFormValues<FormValues>({
    firstName: stubs.firstName,
    lastName: stubs.lastName,
    email: isQueryOwner ? stubs.emailOwner : stubs.emailBorrower,
    password: stubs.password,
    plainPassword: stubs.plainPassword,
    address: null,
    phone: stubs.phone,
    isOfAge: false,
    answerRequests: false,
    purinaBeSentence: '',
    purinaBeOptin: false,
    purinaFrOptin: false,
    ultraPremiumOptin: false,
  })

  const handleIsFocus = (value: boolean) => {
    if (!isUserOnMobile()) return
    setIsFocus(value)
  }

  const onFieldChange = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValue(key, value)
    if (formIsSend) checkField(key, value)
  }

  const checkField = <K extends keyof FormValues>(key: K, value: FormValues[K] = values[key]): boolean => {
    switch (key) {
      case 'firstName':
        if (!value || checkSignUpStringFormat(value as string)) {
          setError(key, t(`sign-up.details.errors.invalidFirstName`))
          return false
        }

        if (containsPhoneNumber(value as string)) {
          setError(key, t(`sign-up.details.errors.fieldPhoneInvalid`))
          return false
        }

        setError(key, '')
        return true

      case 'lastName':
        if (!value || checkSignUpStringFormat(value as string)) {
          setError(key, t(`sign-up.details.errors.invalidName`))
          return false
        }

        if (containsPhoneNumber(value as string)) {
          setError(key, t(`sign-up.details.errors.fieldPhoneInvalid`))
          return false
        }

        setError(key, '')
        return true

      case 'isOfAge':
        if (!value) {
          setError(key, t(`sign-up.details.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'answerRequests':
        if (!value) {
          setError(key, t(`sign-up.details.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'email':
        if (!Validator.validate(value as string, Validator.defaultValidateTypes.mail)) {
          setError(key, t(`sign-up.details.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'password':
        if (value !== values.plainPassword) setError('plainPassword', t(`sign-up.details.errors.plainPassword`))
        else setError('plainPassword', '')

        if (!Validator.validate(value as string, Validator.defaultValidateTypes.password.level2)) {
          setError(key, t(`sign-up.details.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'plainPassword':
        if (value !== values.password) {
          setError(key, t(`sign-up.details.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'address':
        if (!value) {
          setError(key, t(`create-your-profile.address-photo.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'phone':
        if (!value) {
          setError(key, t('create-your-profile.address-photo.errors.phoneRequired'))
          return false
        }
        if (
          !Validator.validate(value as string, Validator.defaultValidateTypes.tel.i18n) ||
          !isValidPhoneNumber(value as string)
        ) {
          setError(key, t('create-your-profile.address-photo.errors.phoneInvalid'))
          return false
        }
        setError(key, '')
        return true

      default:
        return false
    }
  }

  const checkForm = () => {
    let ok = true

    if (!checkField('firstName')) ok = false
    if (!checkField('lastName')) ok = false
    if (!checkField('email')) ok = false
    if (!checkField('password')) ok = false
    if (!checkField('plainPassword')) ok = false

    if (type === UrlParamType.Owner) {
      if (!checkField('address')) ok = false
      if (!checkField('phone')) ok = false
    }

    if (!checkField('isOfAge')) ok = false

    if (type === UrlParamType.Pro) {
      if (!checkField('answerRequests')) ok = false
    }

    return ok
  }

  const normalizeEmail = (email: string) => email.trim().toLowerCase()

  const onSignUp = async (fakeAnimal = false, partnerOptins: PartnerOptins.UserPartnerOptinInput[] = []) => {
    setFormIsSend(true)
    if (!checkForm()) return

    try {
      setLoading(true)
      const normalizedEmail = normalizeEmail(values.email)

      let params: Register.Body
      switch (type) {
        case UrlParamType.Owner:
          params = {
            type: 'petowner',
            body: {
              firstName: values.firstName,
              lastName: values.lastName,
              email: normalizedEmail,
              password: {
                password: values.password,
                passwordConfirmation: values.plainPassword,
              },
              address: values.address!,
              phone: values.phone,
              confirmTerms: values.isOfAge,
              optins: [],
            } satisfies Register.PetOwnerBody,
          }
          break

        case UrlParamType.Borrower:
          params = {
            type: 'borrower',
            body: {
              firstName: values.firstName,
              lastName: values.lastName,
              email: normalizedEmail,
              password: {
                password: values.password,
                passwordConfirmation: values.plainPassword,
              },
              confirmTerms: values.isOfAge,
            } satisfies Register.BorrowerBody,
          }
          break

        case UrlParamType.Pro:
          params = {
            type: 'petsitter',
            body: {
              firstname: values.firstName,
              lastname: values.lastName,
              email: normalizedEmail,
              password: values.password,
              passwordConfirmation: values.plainPassword,
              confirmMajority: values.isOfAge,
              confirmToU: values.answerRequests,
            } satisfies Register.PetSitterBody,
          }
          break

        default:
          throw new Error('type not implemented')
      }

      try {
        const loginPayload = {
          email: normalizedEmail,
          password: values.password,
        }

        const me = await AuthServiceHelper.dispatchLoginAsync(dispatch)(loginPayload)

        navigateToNextRoute(me, { fromRegister: true })
        return
      } catch (loginError: unknown) {
        // Do not fallback to registration when backend transiently fails.
        if (AuthServiceHelper.isEntityManagerClosedError(loginError)) {
          throw loginError
        }

        // Le login a échoué :
        // - soit le compte n'existe pas
        // - soit mauvais mot de passe sur un compte existant
        // donc on tente l'inscription
      }

      const me = await AuthServiceHelper.dispatchRegisterAsync(dispatch)(params)

      const userInRegistration = await UserServiceHelper.dispatchGetMyInfoAsync(dispatch)()

      try {
        await postPartnerOptins(partnerOptins)
      } catch (partnerOptinError: any) {
        console.warn('Partner optins post failed', partnerOptinError?.message)
      }
      await dispatchOptin(me)

      setAddress(values.address)

      // New tags GTM
      const memberRole = isQueryOwner ? 'maitre' : isQueryBorrower ? 'emprunteur' : isQueryPetSitter ? 'pro' : ''
      const eventName =
        memberRole === 'emprunteur'
          ? 'member_registration_borrower'
          : memberRole === 'maitre'
          ? 'member_registration_dogowner'
          : memberRole === 'pro'
          ? 'member_registration_petsitter'
          : ''

      // Ajouter l'event gtag
      gtmSendCustomEvent('member_registration', {
        member_role_variable: memberRole,
        member_id_variable: me.id || userInRegistration.id,
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
        appType: appType,
      })

      // Send custom GTM event
      gtmSendCustomEvent(eventName, {
        member_role_variable: memberRole,
        member_id_variable: me.id || userInRegistration.id,
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
        appType: appType,
      })

      // Fire Google Ads conversion event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        const conversionId =
          memberRole === 'emprunteur' ? 'AW-860378517/9OedCOzRoqwBEJWroZoD' : 'AW-860378517/miDBCMGllqwBEJWroZoD'

        window.gtag('event', 'conversion', {
          send_to: conversionId,
        })
      }
      // END New tags GTM

      // FB tracking
      fbEvent({
        eventName: 'CompleteRegistration', // ViewContent, AddToCart, InitiateCheckout, Purchase etc.
        emails: [values.email || ''], // optional
        firstName: values.firstName || '', // optional
        lastName: values.lastName || '', // optional
        eventId: me.id || userInRegistration.id, // optional
        enableStandardPixel: true,
      })

      const userRoleObj = isQueryOwner
        ? ({ userRole: 'dog owner' } as const)
        : isQueryBorrower
        ? ({ userRole: 'borrower' } as const)
        : isQueryPetSitter
        ? ({ userRole: 'pet sitter' } as const)
        : undefined

      // Webview tracking
      if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined') {
        const eventData: MessageEventData_Login = {
          event: 'register',
          userId: me.id || userInRegistration.id,
          ...userRoleObj,
          userData: {
            email: values.email || '',
            firstName: values.firstName || '',
            lastName: values.lastName || '',
          },
        }
        window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
      }

      // Other webview tracking custom event through a native SDK
      // Borrower and dogowner
      if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined') {
        let eventData: MessageEventData_TrackEvent = {
          event: 'trackFirebaseEvent',
          eventName: isQueryOwner ? 'M Inscription photo OK Maitre' : 'M Inscription photo OK Emprunteur',
          params: {
            userId: me.id || userInRegistration.id,
            ...userRoleObj,
            email: values.email || '',
            firstName: values.firstName || '',
            lastName: values.lastName || '',
          },
        }
        window.ReactNativeWebView.postMessage(JSON.stringify(eventData))

        eventData = {
          event: 'trackFacebookEvent',
          eventName: isQueryOwner ? 'M Inscription photo OK Maitre' : 'M Inscription photo OK Emprunteur',
          params: {
            userId: me.id || userInRegistration.id,
            ...userRoleObj,
            email: values.email || '',
            firstName: values.firstName || '',
            lastName: values.lastName || '',
          },
        }
        window.ReactNativeWebView.postMessage(JSON.stringify(eventData))

        // Both borrower and dogowner
        eventData = {
          event: 'trackFirebaseEvent',
          eventName: 'M_Inscription_1',
          params: {
            userId: me.id || userInRegistration.id,
            ...userRoleObj,
            email: values.email || '',
            firstName: values.firstName || '',
            lastName: values.lastName || '',
          },
        }
        window.ReactNativeWebView.postMessage(JSON.stringify(eventData))

        eventData = {
          event: 'trackFacebookEvent',
          eventName: 'M_Inscription_1',
          params: {
            userId: me.id || userInRegistration.id,
            ...userRoleObj,
            email: values.email || '',
            firstName: values.firstName || '',
            lastName: values.lastName || '',
          },
        }
        window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
      }

      if (type === UrlParamType.Owner && fakeAnimal) {
        const fakeBody = await buildFakeAnimalBody()
        await UserServiceHelper.dispatchCreatePendingAnimalAsync(dispatch)(fakeBody)
        await finalizeInscription(false, false)
        if (isPetsittingMode) {
          router.push({
            pathname: Routes.PetSitter.Search,
            query: { registration: true, type: UrlParamType.Owner },
          })
        } else {
          router.push({
            pathname: Routes.PetSitter.Search,
          })
        }
        return
      }

      if (type === UrlParamType.Pro) goNextStep()
      else navigateToNextRoute(me, { fromRegister: true })
    } catch (error: any) {
      manageNotificationError(error)
      console.warn('Sign-Up Error', error?.message)
    } finally {
      setLoading(false)
    }
  }

  return {
    // ui/layout
    isMobile,
    rowGap,
    isFocus,
    handleIsFocus,

    // flags
    isQueryOwner,
    isQueryBorrower,
    isQueryPetSitter,

    // form
    values,
    errors,
    setError,
    isValid,

    // actions
    onFieldChange,
    onSignUp,
    isLoading,

    // optins
    renderOptins,
  }
}
